/**
 * The app's one drawing tool wheel. Hold Tab over any chart that has drawing
 * tools: the wheel opens at the pointer, moving toward a tile highlights it,
 * and releasing Tab arms that tool on the surface under the pointer (or
 * cancels, released in the hub). The wrench in the hub pins the wheel open to
 * rearrange it; Tab again or the wrench again closes it.
 *
 * Mounted once by the app, never per chart: its listeners go through
 * `globalListenerHub`, and the surface is resolved at Tab time from the
 * registry in `toolWheelTargets`. The layout (`slots`) is the host's to
 * persist; this component only reports changes.
 *
 * Tab is claimed only when a drawing surface is under the pointer, focus is
 * not in a field, dialog or menu, and no modifier is held. Shift+Tab is never
 * claimed, so keyboard focus still moves everywhere else. A claimed Tab stays
 * claimed until it comes up: its auto-repeats are swallowed even after a
 * clicked tile closed the wheel, or the browser walks focus across the page.
 * Escape is claimed only in edit mode, to close it; otherwise it belongs to
 * the chart's own cancel chain.
 *
 * A slot holds a drawing tool, which a pick arms, or one of the drawing bar's
 * mode controls (`toolWheelControls`), which a pick runs.
 *
 * Closing fades the wheel out over `WHEEL_EXIT_MS`. The fading copy is inert
 * and lets clicks through, so the click that places a drawing reaches the
 * chart; a new press replaces it at once.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { hubAdd } from '../core/globalListenerHub'
import {
  DEFAULT_TOOL_WHEEL_SLOTS,
  fitWheelToViewport,
  slotFromPoint,
  swapSlot,
  WHEEL_LAYOUT,
} from '../utils/toolWheelGeometry'
import { ToolWheel } from './ToolWheel'
import { ToolWheelSwapMenu } from './ToolWheelSwapMenu'
import { wheelControl } from './toolWheelControls'
import {
  CLOSED_WHEEL,
  WHEEL_EXIT_MS,
  transitionWheel,
  type WheelEvent,
  type WheelState,
} from './toolWheelState'
import {
  TOOL_WHEEL_KEY,
  getToolWheelPointer,
  resolveToolWheelTarget,
  setToolWheelOpen,
  type ToolWheelTarget,
} from './toolWheelTargets'

export interface ToolWheelHostProps {
  /** Overlay names, clockwise from 12 o clock. */
  slots: readonly string[]
  onSlotsChange: (slots: string[]) => void
}

/** Focus inside any of these keeps Tab's normal meaning. */
const FOCUS_OWNERS = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'

function focusOwnsTab(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
    return true
  }
  return target.closest(FOCUS_OWNERS) !== null
}

export function ToolWheelHost({ slots, onSlotsChange }: ToolWheelHostProps) {
  const [state, setState] = useState<WheelState>(CLOSED_WHEEL)
  /** The surface as it was when Tab went down: armed tool and control states. */
  const [surface, setSurface] = useState<ToolWheelTarget | null>(null)
  const [menu, setMenu] = useState<{ slot: number; x: number; y: number } | null>(null)
  /** The last open state, shown fading out after the wheel closes. */
  const [exiting, setExiting] = useState<WheelState | null>(null)
  /** Bumped on every press, so each gesture mounts (and fades in) a fresh wheel. */
  const [gesture, setGesture] = useState(0)

  const stateRef = useRef(state)
  const targetRef = useRef<ToolWheelTarget | null>(null)
  const slotsRef = useRef(slots)
  slotsRef.current = slots
  const menuOpenRef = useRef(false)
  menuOpenRef.current = menu !== null
  /** The wheel claimed the Tab that is down now: swallow its repeats until it comes up. */
  const tabHeldRef = useRef(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const dispatch = useCallback((event: WheelEvent) => {
    const prev = stateRef.current
    const { state: next, pick, cancel } = transitionWheel(prev, event)
    const target = targetRef.current
    if (next !== prev) {
      stateRef.current = next
      setState(next)
      setToolWheelOpen(next.phase !== 'closed')
      clearTimeout(exitTimerRef.current)
      if (next.phase === 'closed') {
        targetRef.current = null
        setMenu(null)
        setExiting(prev)
        exitTimerRef.current = setTimeout(() => setExiting(null), WHEEL_EXIT_MS)
      } else if (prev.phase === 'closed') {
        setExiting(null)
        setGesture((g) => g + 1)
      }
    }
    // Arm outside any state updater: the surface's handler talks to the chart
    // engine, and React may run an updater twice. A control runs instead.
    const tool = pick === null ? undefined : slotsRef.current[pick]
    if (tool && target) {
      const control = wheelControl(tool)
      if (control) control.run(target)
      else target.selectTool(tool)
    }
    // Tab tapped (released in the hub): turn off whatever tool was armed.
    if (cancel && target) target.cancelTool?.()
  }, [])

  useEffect(() => () => clearTimeout(exitTimerRef.current), [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Escape closes edit mode (Chris, 2026-10-08). An open swap menu takes
      // the first press (it closes itself), so this waits for the next. Kept
      // from the chart's own Escape chain, so it neither cancels a drawing
      // nor restores a maximized panel on the way out.
      if (e.key === 'Escape') {
        if (stateRef.current.phase !== 'editing' || menuOpenRef.current) return
        e.preventDefault()
        e.stopPropagation()
        dispatch({ type: 'dismiss' })
        return
      }
      if (e.code !== TOOL_WHEEL_KEY) return
      // A fresh press starts unclaimed, so a lost keyup never swallows a
      // later Tab the wheel did not take.
      if (!e.repeat) tabHeldRef.current = false
      const { phase } = stateRef.current
      // Auto-repeat of the Tab the wheel took: swallow it, whatever the wheel
      // did since (a clicked tile closed it, the wrench pinned it open), or
      // the browser walks focus across the page and rings every control.
      if (phase === 'held' || (e.repeat && tabHeldRef.current)) {
        e.preventDefault()
        return
      }
      if (e.repeat || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return
      if (phase === 'editing') {
        e.preventDefault()
        tabHeldRef.current = true
        dispatch({ type: 'dismiss' })
        return
      }
      if (focusOwnsTab(e.target)) return
      const point = getToolWheelPointer()
      const target = point ? resolveToolWheelTarget(point) : null
      if (!point || !target) return
      e.preventDefault()
      tabHeldRef.current = true
      targetRef.current = target
      setSurface(target)
      const fit = fitWheelToViewport(
        point,
        { width: window.innerWidth, height: window.innerHeight },
        WHEEL_LAYOUT.size,
      )
      dispatch({ type: 'press', origin: { x: fit.x, y: fit.y }, scale: fit.scale })
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== TOOL_WHEEL_KEY) return
      const claimed = tabHeldRef.current
      tabHeldRef.current = false
      if (stateRef.current.phase === 'held') {
        e.preventDefault()
        dispatch({ type: 'release' })
      } else if (claimed) {
        e.preventDefault()
      }
    }

    const onMove = (e: MouseEvent) => {
      const s = stateRef.current
      if (s.phase === 'closed' || menuOpenRef.current) return
      const p = { x: e.clientX, y: e.clientY }
      let slot = slotFromPoint(p, s.origin, s.scale, slotsRef.current.length)
      // Held, direction alone picks. Editing, only a pointer on the wheel
      // highlights, since clicks there are what open the swap menu.
      if (
        s.phase === 'editing' &&
        Math.hypot(p.x - s.origin.x, p.y - s.origin.y) > (WHEEL_LAYOUT.size / 2) * s.scale
      ) {
        slot = null
      }
      if (slot !== s.hovered) dispatch({ type: 'hover', slot })
    }

    const offs = [
      hubAdd<KeyboardEvent>('window', 'keydown', onKeyDown, { capture: true }),
      hubAdd<KeyboardEvent>('window', 'keyup', onKeyUp, { capture: true }),
      hubAdd<MouseEvent>('window', 'pointermove', onMove),
      hubAdd('window', 'blur', () => {
        tabHeldRef.current = false
        dispatch({ type: 'blur' })
      }),
    ]
    return () => {
      for (const off of offs) off()
      setToolWheelOpen(false)
    }
  }, [dispatch])

  const shown = state.phase === 'closed' ? exiting : state
  if (!shown) return null

  const openMenu = (slot: number, e: { clientX: number; clientY: number }) => {
    if (stateRef.current.phase === 'editing') setMenu({ slot, x: e.clientX, y: e.clientY })
  }
  const canReset = slots.some((name, i) => name !== DEFAULT_TOOL_WHEEL_SLOTS[i])

  return createPortal(
    <>
      <ToolWheel
        key={gesture}
        slots={slots}
        state={shown}
        exiting={state.phase === 'closed'}
        activeTool={surface?.activeTool ?? null}
        controls={surface}
        menuSlot={menu?.slot ?? null}
        canReset={canReset}
        onTileClick={(slot, e) => {
          if (stateRef.current.phase === 'editing') openMenu(slot, e)
          else dispatch({ type: 'click', slot })
        }}
        onTileContextMenu={openMenu}
        onToggleEdit={() => dispatch({ type: 'toggleEdit' })}
        onReset={() => onSlotsChange([...DEFAULT_TOOL_WHEEL_SLOTS])}
      />
      {menu && (
        <ToolWheelSwapMenu
          x={menu.x}
          y={menu.y}
          slot={menu.slot}
          slots={slots}
          onPick={(tool) => {
            onSlotsChange(swapSlot(slots, menu.slot, tool))
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </>,
    document.body,
  )
}
